import { useContext, useState } from 'react';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  styled,
  Checkbox,
  Grid,
  Link,
  TableCell,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { participantAttendedSession } from "@components/Pages/Events/Sessions/sessionFunctions";
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from '@lib/contexts/pageContext';
import { paletteMains, scrollToAlert } from "@lib/utils";

const NameCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  borderColor: '#0000001f',
  minWidth: '250px'
});

const DataCell = styled(TableCell)({
  backgroundColor: '#ffffff',
  border: '1px solid #0000001f',
  textAlign: 'center',
  '& > svg': {
    marginTop: '4px',
  }
});

const ParticipantRow = ({ participant, session }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [checked, setChecked] = useState(!!participantAttendedSession(participant, session));
  const [prevValue, setPrevValue] = useState(participantAttendedSession(participant, session));
  const queryClient = useQueryClient();

  const theme = useTheme();
  const palette = paletteMains(theme);

  const {
    setPageErrorMessage,
  } = useContext(PageContext);

  const saveMutation = useMutation({
    mutationFn: async (status) => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.PARTICIPANTS}/${participant.id}/attendance`,
      data: {
        session_id: session.id,
        status,
      }
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['participants', session.event_id] }),
      ]);
    },
    onError: async (err) => {
      setPageErrorMessage(['Error saving attendance', err]);
      console.log('[FinalizeParticipants][save] error:', err);
      // Roll back the optimistic update.
      setChecked(prevValue);
      scrollToAlert();
    },
    onSettled: () => {
      setIsSaving(false);
    },
  });

  const handleChange = (ev) => {
    // debounce
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    // This is an optimistic update to show the new status before the API request finishes.
    setPrevValue(checked);
    setChecked(ev.target.checked);
    saveMutation.mutate(ev.target.checked ? 'PRESENT' : 'ABSENT');
  }

  return (
    <TableRow>
      <NameCell>
        <Grid container>
          <Grid item xs={8} data-cy="participant-name">
            {participant.first_name} {participant.middle_name} {participant.last_name}
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Link
              component={RouterLink}
              to={`/events/${session.event_id}/${session.id}/participants/${participant.id}/edit`}
              sx={{ color: palette['il-industrial'], }}>
              Edit
            </Link>
          </Grid>
        </Grid>
      </NameCell>
      <DataCell>
        {participant.demographics_collected ?
          (
            <Tooltip title="Collected">
              <CheckCircleIcon color="success"/>
            </Tooltip>)
          :
          (
            <Tooltip title="Not collected">
              <ErrorIcon color="yellow"/>
            </Tooltip>
          )}
      </DataCell>
      <DataCell sx={{padding: 0}}><Checkbox checked={checked} onChange={handleChange}/></DataCell>
    </TableRow>
  );
};

export default ParticipantRow;
